import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useHandlers,
  useHiddenCount,
  useVisibilitySwitchers,
} from '../../lib/multi-select';
import { EElementSize } from '../../types';
import { Checkbox } from '../checkbox';
import { DropContent } from '../drop-content';
import { DropDown } from '../drop-down';
import { useDropDownContext } from '../drop-down';
import { DropDownProvider } from '../drop-down/drop-down.provider';
import { DropHeader } from '../drop-header';
import { FieldText } from '../field-text';
import { SearchIcon, ShevronDownIcon } from '../icons';
import { InputWrapper } from '../input-wrapper';
import { SelectShevronAction } from '../select-shevron-action';
import { Tag } from '../tag';
import {
  MultiSelectFieldStyled,
  MultiSelectListItemStyled,
  MultiSelectListStyled,
} from './multi-select.styled';
import {
  IMultiSelectFieldProps,
  IMultiSelectListProps,
  IMultiSelectProps,
  TMultiSelectItemSelectable,
} from './multi-select.types';

function MultiSelectList<T>({
  options,
  value,
  onChange,
}: IMultiSelectListProps<T>) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const filteredList = useMemo(
    () =>
      (searchText
        ? options.filter(el =>
            el.caption
              .toLocaleLowerCase()
              .includes(searchText.toLocaleLowerCase()),
          )
        : options
      ).map(el => ({ ...el, isSelected: value.includes(el.value) })),
    [options, searchText, value],
  );

  const itemClickHandler = useCallback(
    (item: TMultiSelectItemSelectable<T>, checked: boolean) => {
      if (!onChange) {
        return;
      }

      if (!checked) {
        onChange(value.filter(el => el !== item.value));
      } else {
        onChange([...value, item.value]);
      }
    },
    [onChange, value],
  );

  return (
    <DropContent>
      <DropHeader $size={EElementSize.S}>
        <FieldText
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          $size={EElementSize.S}
          icon={<SearchIcon />}
          placeholder={t('search')}
        />
      </DropHeader>
      <MultiSelectListStyled>
        {filteredList.map(el => (
          <MultiSelectListItemStyled
            key={el.key || String(el.value)}
            $isSelected={el.isSelected}
          >
            <Checkbox
              checked={el.isSelected}
              onChange={() => itemClickHandler(el, !el.isSelected)}
            >
              {el.caption}
            </Checkbox>
          </MultiSelectListItemStyled>
        ))}
      </MultiSelectListStyled>
    </DropContent>
  );
}

function MultiSelectField<T>({
  selected,
  onChange = () => {},
  disabled,
}: IMultiSelectFieldProps<T>) {
  const chipsRef = useRef<HTMLDivElement>(null);
  const { hiddenCount } = useHiddenCount<T>(selected, chipsRef);
  const { isOpen } = useDropDownContext();
  const { tagRemoveHandler } = useHandlers<T>(selected, onChange);

  const { isMoreCountShown } = useVisibilitySwitchers<T>(selected, hiddenCount);

  return (
    <MultiSelectFieldStyled $isOpen={isOpen} $isDisabled={disabled}>
      <div className="chips" ref={chipsRef}>
        {selected.map(el => (
          <Tag
            className="tag"
            onClose={() => tagRemoveHandler(el)}
            key={String(el.value)}
          >
            {el.caption}
          </Tag>
        ))}
      </div>
      <div className="actions">
        {isMoreCountShown && (
          <span className="more-count">Еще {hiddenCount}</span>
        )}
      </div>
    </MultiSelectFieldStyled>
  );
}

export function MultiSelect<T>({
  options,
  value,
  placeholder,
  onChange = () => {},
  width,
  offset = 4,
  disabled,
  isOverToggler,
  positionRef,
  children,
  $size = EElementSize.M,
}: IMultiSelectProps<T>) {
  const selectedItems = useMemo(() => {
    return options.filter(el => value.includes(el.value));
  }, [options, value]);

  return (
    <DropDownProvider>
      {disabled ? (
        children || (
          <FieldText
            placeholder={placeholder}
            disabled
            readOnly
            width={width}
            actionButtons={<ShevronDownIcon />}
          />
        )
      ) : (
        <DropDown
          content={
            <MultiSelectList<T>
              options={options}
              value={value}
              onChange={onChange}
            />
          }
          positionRef={positionRef}
          offset={offset}
          isOverToggler={isOverToggler}
          placement="bottom-start"
        >
          {children || (
            <InputWrapper
              $size={$size}
              disabled={disabled}
              actionButtons={<SelectShevronAction />}
              width={width}
              isClearable={selectedItems.length > 0}
              cursor="pointer"
              placeholder={placeholder}
              onClear={() => onChange([])}
            >
              {selectedItems.length > 0 ? (
                <MultiSelectField<T>
                  onChange={onChange}
                  disabled={disabled}
                  selected={selectedItems}
                />
              ) : (
                <span className="placeholder">{placeholder}</span>
              )}
            </InputWrapper>
          )}
        </DropDown>
      )}
    </DropDownProvider>
  );
}
